// ============================================================================
// PROJECT EXPENSES TAB
// Consolidated expenses from all child jobs and appointments
// Per-category and per-job breakdowns with summary cards
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Receipt, Fuel, ShoppingCart, Car, Wrench, DollarSign,
  ChevronDown, ChevronRight, RefreshCw, Briefcase, BarChart3,
  Image, ExternalLink,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import type { WorkItem } from '../UniversalWorkItems';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectExpensesTabProps {
  workItem: WorkItem;
}

interface Expense {
  id: string;
  item_type: string;
  item_number: string;
  title: string;
  status: string;
  amount: number;
  category: string;
  job_id: string;
  job_number: string;
  job_title: string;
  description: string;
  receipt_url: string | null;
  vendor: string | null;
  expense_date: string;
  created_at: string;
  custom_fields: Record<string, any>;
}

interface CategoryAgg {
  category: string;
  total: number;
  count: number;
}

interface JobExpAgg {
  job_id: string;
  job_number: string;
  job_title: string;
  job_status: string;
  total: number;
  count: number;
}

interface ExpenseSummary {
  total_amount: number;
  expense_count: number;
  fuel_total: number;
  purchase_total: number;
  toll_total: number;
  maintenance_total: number;
  other_total: number;
  jobs_count: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VIEWS = ['overview', 'by_job', 'all'] as const;
type ViewId = typeof VIEWS[number];

const viewMeta: Record<ViewId, { label: string; icon: React.ElementType }> = {
  overview: { label: 'Overview', icon: BarChart3 },
  by_job: { label: 'By Job', icon: Briefcase },
  all: { label: 'All Expenses', icon: Receipt },
};

const fmt = (n: number) => `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const categoryIcons: Record<string, React.ElementType> = {
  fuel: Fuel,
  purchase: ShoppingCart,
  toll: Car,
  maintenance: Wrench,
  vehicle: Car,
  other: DollarSign,
};

const categoryColors: Record<string, string> = {
  fuel: 'amber',
  purchase: 'blue',
  toll: 'purple',
  maintenance: 'red',
  vehicle: 'indigo',
  other: 'gray',
};

const categoryLabel = (c: string) => c?.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()) || 'Other';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProjectExpensesTab({ workItem }: ProjectExpensesTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  // State
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<ViewId>('overview');
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [byCategory, setByCategory] = useState<CategoryAgg[]>([]);
  const [byJob, setByJob] = useState<JobExpAgg[]>([]);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  // Helpers
  const getHeaders = useCallback(() => {
    const tokens = authService.getTokens();
    return {
      'Content-Type': 'application/json',
      ...(tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {}),
    };
  }, []);

  // Fetch
  const fetchExpenses = useCallback(async () => {
    if (!workItem.id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/projects/${workItem.id}/expenses`, { headers: getHeaders() });
      const j = await res.json();
      if (j.success) {
        setSummary(j.data.summary);
        setExpenses(j.data.expenses || []);
        setByCategory(j.data.by_category || []);
        setByJob(j.data.by_job || []);
      }
    } catch (err) {
      console.error('Failed to fetch project expenses', err);
    } finally {
      setLoading(false);
    }
  }, [workItem.id, getHeaders]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const toggleJob = (id: string) => setExpandedJobs(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-base font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
            Project Expenses
          </h3>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Consolidated from all child jobs &amp; appointments
          </p>
        </div>
        <button
          onClick={fetchExpenses}
          disabled={loading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            isDark
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:text-gray-500'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:text-gray-400'
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total Expenses', value: fmt(summary.total_amount), icon: DollarSign, color: 'blue',
              sub: `${summary.expense_count} items` },
            { label: 'Fuel', value: fmt(summary.fuel_total), icon: Fuel, color: 'amber' },
            { label: 'Purchases', value: fmt(summary.purchase_total), icon: ShoppingCart, color: 'green' },
            { label: 'Maintenance', value: fmt(summary.maintenance_total), icon: Wrench, color: 'red' },
          ].map(card => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</span>
                  <Icon className={`w-4 h-4 text-${card.color}-500`} />
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{card.value}</div>
                {card.sub && (
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{card.sub}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View Tabs */}
      <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {VIEWS.map(v => {
          const Meta = viewMeta[v];
          const Icon = Meta.icon;
          const isActive = activeView === v;
          return (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition ${
                isActive
                  ? isDark ? 'border-blue-500 text-blue-400' : 'border-blue-600 text-blue-600'
                  : isDark ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {Meta.label}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading expenses…
        </div>
      )}

      {/* Empty */}
      {!loading && summary && summary.expense_count === 0 && (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No expenses recorded</p>
          <p className="text-sm mt-1">Expenses will appear here once added to child jobs.</p>
        </div>
      )}

      {/* ======================== OVERVIEW ======================== */}
      {!loading && activeView === 'overview' && byCategory.length > 0 && (
        <div className="space-y-4">
          {/* Category breakdown with bars */}
          <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Expenses by Category</h4>
            </div>
            <div className="p-4 space-y-3">
              {byCategory.map(cat => {
                const pct = summary && summary.total_amount > 0
                  ? (cat.total / summary.total_amount * 100)
                  : 0;
                const Icon = categoryIcons[cat.category] || DollarSign;
                const color = categoryColors[cat.category] || 'gray';
                return (
                  <div key={cat.category} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 text-${color}-500`} />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {categoryLabel(cat.category)}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({cat.count})</span>
                      </div>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {fmt(cat.total)} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div className={`h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className={`h-2 rounded-full bg-${color}-500 transition-all`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Jobs summary */}
          {byJob.filter(j => j.count > 0).length > 0 && (
            <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Top Jobs by Expense</h4>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {byJob.filter(j => j.count > 0).slice(0, 5).map(j => (
                  <div key={j.job_id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{j.job_number}</span>
                      <span className={`text-sm font-medium ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{j.job_title}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{j.count} expense{j.count !== 1 ? 's' : ''}</span>
                    </div>
                    <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(j.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================== BY JOB ======================== */}
      {!loading && activeView === 'by_job' && (
        <div className="space-y-2">
          {byJob.map(j => {
            const isExpanded = expandedJobs.has(j.job_id);
            const jobExpenses = expenses.filter(e => e.job_id === j.job_id);
            return (
              <div key={j.job_id} className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => toggleJob(j.job_id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{j.job_number}</span>
                      <span className={`text-sm font-semibold ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{j.job_title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                      {j.count} expense{j.count !== 1 ? 's' : ''}
                    </span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(j.total)}</span>
                  </div>
                </button>
                {isExpanded && (
                  <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {jobExpenses.length > 0 ? (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {jobExpenses.map(exp => {
                          const CatIcon = categoryIcons[exp.category] || DollarSign;
                          return (
                            <div key={exp.id} className="flex items-center gap-3 px-4 py-2.5">
                              <CatIcon className={`w-4 h-4 flex-shrink-0 text-${categoryColors[exp.category] || 'gray'}-500`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                    {exp.title || exp.description || categoryLabel(exp.category)}
                                  </span>
                                  {exp.vendor && (
                                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{exp.vendor}</span>
                                  )}
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {exp.expense_date ? new Date(exp.expense_date).toLocaleDateString() : '—'}
                                  {exp.item_number && <span className="ml-2 font-mono">{exp.item_number}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {exp.receipt_url && (
                                  <Image className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                )}
                                <span className={`text-sm font-semibold whitespace-nowrap ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                  {fmt(exp.amount)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className={`px-4 py-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No expenses for this job
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {byJob.length === 0 && !loading && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No job expense data available.</p>
          )}
        </div>
      )}

      {/* ======================== ALL EXPENSES ======================== */}
      {!loading && activeView === 'all' && (
        <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Category</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vendor</th>
                  <th className={`px-4 py-2.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Amount</th>
                  <th className={`px-4 py-2.5 text-center font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Receipt</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {expenses.slice(0, 100).map(exp => {
                  const CatIcon = categoryIcons[exp.category] || DollarSign;
                  return (
                    <tr key={exp.id} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                      <td className={`px-4 py-2 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {exp.expense_date ? new Date(exp.expense_date).toLocaleDateString() : '—'}
                      </td>
                      <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-1.5">
                          <CatIcon className={`w-3.5 h-3.5 text-${categoryColors[exp.category] || 'gray'}-500`} />
                          {categoryLabel(exp.category)}
                        </div>
                      </td>
                      <td className={`px-4 py-2 max-w-[200px] truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {exp.title || exp.description || '—'}
                      </td>
                      <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="font-mono text-xs">{exp.job_number}</span>
                      </td>
                      <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {exp.vendor || '—'}
                      </td>
                      <td className={`px-4 py-2 text-right font-medium whitespace-nowrap ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {fmt(exp.amount)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {exp.receipt_url ? (
                          <Image className={`w-4 h-4 mx-auto ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        ) : (
                          <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {expenses.length === 0 && (
              <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No expenses found</div>
            )}
            {expenses.length > 100 && (
              <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                Showing 100 of {expenses.length} expenses
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
